import { Box, Tab as MuiTab, Paper, Tabs } from '@mui/material';
import { ComponentType, SyntheticEvent, useId, useState } from 'react';
import { useRecordContext, useTranslate } from 'react-admin';

import { LiveLogRange, LiveLogSource } from 'types/LiveLogs';
import { EventLogGrid } from './EventLogGrid';
import { LiveLogList } from './LiveLogList';
import {
  DEFAULT_LIVE_LOG_RANGE,
  DEFAULT_LIVE_LOG_SOURCE,
  isLiveLogSource,
  LIVE_LOG_SOURCES,
  LIVE_LOGS_I18N,
  liveLogResource,
} from './liveLogs';
import { LiveLogsToolbar } from './LiveLogsToolbar';
import { NginxLogGrid } from './NginxLogGrid';
import { UsageLogGrid } from './UsageLogGrid';

const GRIDS: Record<LiveLogSource, ComponentType> = {
  nginx: NginxLogGrid,
  usagelog: UsageLogGrid,
  eventlog: EventLogGrid,
};

/** The tab body. It owns the tab's state, which the Tab wrapper must not (it is cloned twice). */
export const LiveLogsPanel = () => {
  const record = useRecordContext();
  const translate = useTranslate();
  const idPrefix = useId();
  const [range, setRange] = useState<LiveLogRange>(DEFAULT_LIVE_LOG_RANGE);
  const [source, setSource] = useState<LiveLogSource>(DEFAULT_LIVE_LOG_SOURCE);
  const [autoRefresh, setAutoRefresh] = useState(false);

  if (!record) {
    return null;
  }

  const resource = liveLogResource(record.id, source);
  const Grid = GRIDS[source];
  const tabId = (value: LiveLogSource) => `${idPrefix}-tab-${value}`;
  const panelId = (value: LiveLogSource) => `${idPrefix}-panel-${value}`;

  const handleSourceChange = (_event: SyntheticEvent, value: unknown) => {
    if (isLiveLogSource(value)) {
      setSource(value);
    }
  };

  return (
    <Box>
      <LiveLogsToolbar
        resource={resource}
        range={range}
        onRangeChange={setRange}
        autoRefresh={autoRefresh}
        onAutoRefreshChange={setAutoRefresh}
      />
      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={source}
          onChange={handleSourceChange}
          variant="scrollable"
          aria-label={translate(`${LIVE_LOGS_I18N}.source_tabs_label`)}
        >
          {LIVE_LOG_SOURCES.map((value) => (
            <MuiTab
              key={value}
              value={value}
              id={tabId(value)}
              aria-controls={panelId(value)}
              label={translate(`${LIVE_LOGS_I18N}.sources.${value}`)}
            />
          ))}
        </Tabs>
      </Paper>
      {/* One panel per tab, so every aria-controls points at a real element; only the active one mounts a list. */}
      {LIVE_LOG_SOURCES.map((value) => (
        <Box key={value} role="tabpanel" id={panelId(value)} aria-labelledby={tabId(value)} hidden={value !== source}>
          {value === source && (
            <LiveLogList resource={resource} range={range} autoRefresh={autoRefresh}>
              <Grid />
            </LiveLogList>
          )}
        </Box>
      ))}
    </Box>
  );
};
